import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { RiArrowRightUpLine, RiChat3Line, RiCheckboxCircleLine, RiFileTextLine, RiReplyLine } from "@remixicon/react";
import { sessionStatusChip } from "../../utils/studentDetail";
import { eventCount, evidenceRows } from "../../utils/ticketDetail";
import { tokens } from "../../utils/figmaTokens";
import DetailRows from "../../components/kit/DetailRows";
import InitialsAvatar from "../../components/kit/InitialsAvatar";
import SmallButton from "../../components/kit/SmallButton";
import { ArrowLink, NewTabNote, Panel, PanelHead } from "./TicketParts";

// The right column of the ticket page (D62; Right column on 10829:93565), all from stored data.

const rowLook = { labelColor: tokens.text.secondary, line: tokens.divider };

// Session evidence: what the lesson document recorded, or, when it cannot be read (`booking`
// null), the times the ticket copied. `booking` is undefined while it is read. A late join, and a
// join missing once the lesson has ended, is red.
export const EvidenceCard = ({ ticket, booking, canOpenLesson }) => {
  const chip = booking ? sessionStatusChip(booking) : null;
  return (
    <Panel>
      <PanelHead
        title="Session evidence"
        line={booking === null ? "The lesson could not be read. These times were copied onto the ticket." : "What the platform recorded for this lesson."}
        aside={chip && <Chip label={chip.label} color={chip.color} />}
      />
      {booking === undefined ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <DetailRows
          {...rowLook}
          rows={evidenceRows(ticket, booking).map((row) => ({
            ...row,
            value: row.alert ? (
              <Box component="span" sx={{ color: tokens.error.ink }}>
                {row.value}
              </Box>
            ) : (
              row.value
            ),
          }))}
        />
      )}
      {/* A new tab, as the ticket page's other ↗ links (047 D80). */}
      {canOpenLesson && (
        <SmallButton
          tone="ghost"
          EndIcon={RiArrowRightUpLine}
          component={RouterLink}
          to={`/lessons/${ticket.bookingId}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ alignSelf: "flex-start" }}
        >
          Open lesson
          <NewTabNote />
        </SmallButton>
      )}
    </Panel>
  );
};

// A student or tutor card (Family, Teacher): the avatar, the name and a line under it, one row,
// and the link to their page when the viewer may open it. `card` is one of ticketDetail.personCards.
export const PersonCard = ({ card, canOpen }) => (
  <Panel>
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <InitialsAvatar name={card.name} colorKey={card.id} size={40} />
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
        <Typography variant="titleCard" component="h2" sx={{ color: tokens.text.primary, overflowWrap: "anywhere" }}>
          {card.name}
        </Typography>
        <Typography variant="labelMedium" component="div" sx={{ color: tokens.text.secondary }}>
          {card.line}
        </Typography>
      </Box>
    </Box>
    <DetailRows {...rowLook} rows={[card.row]} />
    {canOpen && <ArrowLink to={card.to}>{card.linkLabel}</ArrowLink>}
  </Panel>
);

const RINGS = {
  brand: [tokens.brand.subtle, tokens.icon.brand],
  success: [tokens.success.bg, tokens.success.main],
  neutral: [tokens.surface.muted, tokens.icon.default],
};
const EVENT_ICONS = { opened: RiFileTextLine, reply: RiReplyLine, message: RiChat3Line, resolved: RiCheckboxCircleLine };

// Timeline (Admin · Activity Item rows): a ringed icon, what happened and by whom, and when.
// `events` is ticketDetail.timelineEvents.
export const TimelineCard = ({ events }) => (
  <Panel>
    <PanelHead title="Timeline" aside={<Chip label={eventCount(events.length)} />} />
    <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none" }}>
      {events.map((event) => {
        const [bg, color] = RINGS[event.tone] || RINGS.neutral;
        const Icon = EVENT_ICONS[event.icon] || RiChat3Line;
        return (
          <Box component="li" key={event.key} sx={{ display: "flex", alignItems: "flex-start", gap: "11px", px: "2px", py: "11px" }}>
            <Box aria-hidden sx={{ display: "flex", p: "7px", borderRadius: "999px", bgcolor: bg, color, flexShrink: 0 }}>
              <Icon size={15} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
              <Typography variant="bodySmall" sx={{ color: tokens.text.primary }}>
                {event.title}
              </Typography>
              {event.sub && (
                <Typography variant="labelMedium" component="div" sx={{ color: tokens.text.tertiary }}>
                  {event.sub}
                </Typography>
              )}
            </Box>
            <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary, whiteSpace: "nowrap" }}>
              {event.time}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Panel>
);
